import { useCallback } from 'react';
import { getCrossID, getRealID } from 'utils/filters';
import { logEvent } from 'utils/tracking';
import { SENT_TO_BE_AS_STANDARD_FILTERS, RANGE_FIELDS } from '../constants';

/**
 * Custom hook for filter operations (handlers)
 *
 * This hook provides a set of filter operation handlers for managing entity filters,
 * including creating, updating, and deleting filters. It supports both standard filters
 * and cross-entity filters, with optional analytics tracking and storage management.
 *
 * @param {Object} config - Configuration object containing all necessary dependencies
 * @param {Object} config.entity - The main entity being filtered
 * @param {boolean} config.avoidStorage - Whether to skip storage operations and use custom handlers
 * @param {boolean} config.useQuickFilters - Whether this is being used for quick filters
 * @param {Object} config.operators - Standard operators for the main entity
 * @param {Object} config.crossOperators - Operators for cross-entity filters
 * @param {Function} config.getHasAdvancedFilters - Function to check if advanced filters are enabled
 * @param {Function} config.formatRangeValueToSave - Function to format range values before saving
 * @param {Function} config.handleSubfilters - Function to handle subfilter operations
 * @param {Object} config.values - Current filter values
 * @param {Function} config.changeFilter - Standard function to change filters
 * @param {Function} config.customChangeFilter - Custom function to change filters (when avoidStorage is true)
 * @param {Object} config.relatedEntity - Related entity information
 * @param {Function} config.dispatch - Redux dispatch function
 * @param {Function} config.saveNewCache - Function to save filter configuration to cache
 * @param {Array} config.quickFilters - Array of quick filter IDs
 * @param {Array} config.visibleFilters - Array of visible filter IDs
 * @param {Object} config.openFilters - Object mapping filter IDs to their open state
 * @param {Object} config.openQuickFilters - Object mapping quick filter IDs to their open state
 * @param {Object} config.crossFilters - Cross-entity filter configurations
 * @param {Object} config.filters - Standard filter configurations
 * @param {Function} config.changeAdvancedFilter - Function to change advanced filter operators
 * @param {Function} config.customChangeOperator - Custom function to change operators (when avoidStorage is true)
 * @param {Function} config.disableForceCrossFilters - Function to disable forced cross filters
 * @param {Function} config.deleteMultipleFilters - Function to delete multiple filters at once
 * @param {Function} config.customDeleteMultipleFilters - Custom function to delete multiple filters
 * @param {Function} config.clearFilters - Function to clear all filters
 * @param {Function} config.customClearFilters - Custom function to clear filters (when avoidStorage is true)
 * @param {Function} config.clearSpecificFilter - Function to clear a specific filter
 *
 * @returns {Object} Object containing filter operation handlers:
 *   - onChangeFilter: Handler for changing/updating filter values
 *   - onDeleteFilter: Handler for deleting a single filter
 *   - onDeleteMultipleFilters: Handler for deleting multiple filters
 *   - onClearFilters: Handler for clearing all filters
 */
const useFilterOperations = ({
    entity,
    avoidStorage,
    useQuickFilters,
    operators,
    crossOperators,
    getHasAdvancedFilters,
    formatRangeValueToSave,
    handleSubfilters,
    values,
    changeFilter,
    customChangeFilter,
    relatedEntity,
    dispatch,
    saveNewCache,
    quickFilters,
    visibleFilters,
    openFilters,
    openQuickFilters,
    crossFilters,
    filters,
    changeAdvancedFilter,
    customChangeOperator,
    disableForceCrossFilters,
    deleteMultipleFilters,
    customDeleteMultipleFilters,
    clearFilters,
    customClearFilters,
    clearSpecificFilter,
}) => {
    /**
     * Handler for changing/updating filter values
     *
     * This function processes filter value changes, handles value formatting,
     * tracks analytics events, and updates the filter state.
     *
     * @param {Object} params - Parameters for filter change
     * @param {Object} params.filter - Filter configuration object
     * @param {any} params.value - New filter value (can be single value or array)
     * @param {Object} [params.info] - Additional information about the change
     * @param {string} [params.crossEntity] - Cross-entity identifier if this is a cross-entity filter
     */
    const onChangeFilter = useCallback(
        ({ filter, value, info, crossEntity }) => {
            let completeValues = null;
            let newValue = null;

            // Determine which operators to use (cross-entity or standard)
            const currentOperators = !!crossEntity
                ? crossOperators?.[crossEntity] || {}
                : operators;
            const hasOperator =
                getHasAdvancedFilters(crossEntity) &&
                currentOperators &&
                !!currentOperators?.[filter.id];
            const isCrossFilter = !!crossEntity;

            // Format range values if needed
            if (RANGE_FIELDS.includes(filter.dataType)) {
                value = formatRangeValueToSave(value, filter);
            }

            // Process array values (multiple selections)
            if (value && Array.isArray(value)) {
                completeValues = value.map((current) => {
                    if (!newValue) newValue = [];
                    const valueToUse =
                        current.value !== undefined && current.value !== null
                            ? current.value
                            : current;
                    newValue.push(valueToUse);
                    return {
                        value: valueToUse,
                        label: current.label,
                        idParent: current.idParent || current.idparent,
                    };
                });
            } else {
                // Process single values
                newValue = value?.value || value;
                completeValues = value;
            }

            // Apply custom value formatting if provided
            if (filter.customFormatValueToSave) {
                newValue = filter.customFormatValueToSave(newValue);
            }

            if (filter.customFormatCompleteValuesToSave) {
                completeValues = filter.customFormatCompleteValuesToSave(completeValues, filter);
            }

            const inputAttrs = filter.inputAttrs || null;

            // Handle minimum selection requirements with backup values
            if (
                (!newValue ||
                    (inputAttrs?.minSelection && inputAttrs?.minSelection > newValue.length)) &&
                inputAttrs?.backupSelection
            ) {
                newValue = inputAttrs.backupSelection?.value || null;
                completeValues = inputAttrs.backupSelection?.completeValues || null;
            }

            // Analytics tracking
            if (!avoidStorage) {
                if (useQuickFilters) {
                    logEvent({ event: entity.trueName, functionality: 'quickFilter' });
                    isCrossFilter &&
                        logEvent({
                            event: entity.trueName,
                            functionality: 'relatedEntityFilter',
                        });
                    hasOperator &&
                        logEvent({
                            event: entity.trueName,
                            functionality: 'advancedFilters',
                        });
                } else {
                    if (newValue && newValue?.length) {
                        logEvent({
                            event: entity.trueName,
                            functionality: isCrossFilter ? 'relatedEntityFilter' : 'filter',
                        });
                        hasOperator &&
                            logEvent({
                                event: entity.trueName,
                                functionality: 'advancedFilters',
                            });
                    }
                }
            }

            // Choose appropriate change filter function based on storage mode
            const finalChangeFilter =
                avoidStorage && customChangeFilter ? customChangeFilter : changeFilter;

            // Prepare payload for filter change
            const payload = {
                entity,
                filter,
                value: newValue,
                refresh: undefined,
                completeValues,
                isEntityList: null,
                info,
                isPreload: false,
                relatedEntity,
                crossEntity,
            };

            finalChangeFilter(payload);

            // Handle subfilters if this filter has them
            if (filter.hasOwnProperty('subfilterKey')) {
                const crossId = getCrossID(filter.id, crossEntity);
                handleSubfilters({
                    values: newValue,
                    oldValues: values[crossId] || [],
                    filter: filter.id,
                    crossEntity,
                });
            }
        },
        [
            crossOperators,
            operators,
            getHasAdvancedFilters,
            useQuickFilters,
            handleSubfilters,
            values,
            changeFilter,
            avoidStorage,
            customChangeFilter,
            entity,
            relatedEntity,
            formatRangeValueToSave,
        ],
    );

    /**
     * Handler for deleting a single filter
     *
     * This function removes a filter from the UI configuration, clears its values,
     * removes operators, and handles cross-entity filter cleanup.
     *
     * @param {string} filter - The filter ID to delete
     */
    const onDeleteFilter = useCallback(
        (filter) => {
            // Remove filter from all UI configuration arrays/objects
            const newVisibleFilters = visibleFilters.filter((f) => f !== filter);
            const newQuickFilters = quickFilters.filter((f) => f !== filter);
            const newOpenFilters = Object.keys(openFilters).reduce((obj, key) => {
                if (key === filter) return obj;
                obj[key] = openFilters[key];
                return obj;
            }, {});
            const newOpenQuickFilters = Object.keys(openQuickFilters).reduce((obj, key) => {
                if (key === filter) return obj;
                obj[key] = openQuickFilters[key];
                return obj;
            }, {});

            const config = {
                openFilters: newOpenFilters,
                openQuickFilters: newOpenQuickFilters,
                visibleFilters: newVisibleFilters,
                quickFilters: newQuickFilters,
            };

            if (!avoidStorage) {
                dispatch({ type: 'saveConfig', payload: config });
                logEvent({ event: entity.trueName, functionality: 'deleteFilter' });
            }

            // Save configuration and then clean up filter data
            saveNewCache(config).finally(() => {
                const { id: filterId, entity: crossEntity } = getRealID(filter);

                // Determine if this is a cross-entity filter that should be treated as such
                const finalCrossEntity =
                    crossEntity && !SENT_TO_BE_AS_STANDARD_FILTERS.includes(filterId)
                        ? crossEntity
                        : null;

                // Get filter and operator objects
                const filterObj = finalCrossEntity
                    ? crossFilters?.[finalCrossEntity]?.[filterId]
                    : filters[filterId];

                const finalOperators = finalCrossEntity
                    ? crossOperators?.[finalCrossEntity]
                    : operators;

                const operatorsObj = finalOperators?.[filterId];

                // Disable forced cross filters if this was a cross-entity filter
                if (!!crossEntity && !avoidStorage) {
                    disableForceCrossFilters(entity);
                }

                // Clear the filter value
                if (filterObj) {
                    onChangeFilter({
                        filter: filterObj,
                        value: null,
                        crossEntity: finalCrossEntity,
                    });
                }

                // Remove the operator if it exists
                if (operatorsObj) {
                    const { [filterId]: removed, ...operatorsRest } = finalOperators;

                    const payload = {
                        entity,
                        operators: operatorsRest,
                        shouldUpdateEntity: !filterObj,
                        crossEntity: finalCrossEntity,
                    };

                    const finalChangeAdvancedFilter =
                        avoidStorage && customChangeOperator
                            ? customChangeOperator
                            : changeAdvancedFilter;

                    finalChangeAdvancedFilter(payload);
                }
            });
        },
        [
            dispatch,
            visibleFilters,
            quickFilters,
            openFilters,
            openQuickFilters,
            entity,
            saveNewCache,
            crossFilters,
            filters,
            crossOperators,
            operators,
            onChangeFilter,
            changeAdvancedFilter,
            disableForceCrossFilters,
            avoidStorage,
            customChangeOperator,
        ],
    );

    /**
     * Handler for deleting multiple filters at once
     *
     * This function delegates to either the standard or custom delete multiple filters function
     * based on the storage mode.
     *
     * @param {Array} filters - Array of filter IDs to delete
     */
    const onDeleteMultipleFilters = useCallback(
        (filters) => {
            const payload = { entity, filters };

            const finalDeleteMultipleFilters =
                avoidStorage && customDeleteMultipleFilters
                    ? customDeleteMultipleFilters
                    : deleteMultipleFilters;

            finalDeleteMultipleFilters(payload);
        },
        [entity, deleteMultipleFilters, customDeleteMultipleFilters, avoidStorage],
    );

    /**
     * Handler for clearing all filters
     *
     * This function clears all filters for the entity, handles session storage cleanup,
     * logs analytics events, and delegates to appropriate clear functions based on storage mode.
     */
    const onClearFilters = useCallback(() => {
        // Handle session storage cleanup and analytics tracking
        if (!avoidStorage) {
            if (filters.customView) {
                sessionStorage.removeItem(filters.customView.value.sessionStorageLabel);
            }

            if (useQuickFilters) {
                logEvent({
                    event: entity.trueName,
                    functionality: 'quickClearFilter',
                });
            } else {
                logEvent({ event: entity.trueName, functionality: 'clearFilter' });
            }
        }

        // Clear specific filter if provided
        if (clearSpecificFilter) {
            clearSpecificFilter();
        }

        // Choose appropriate clear function based on storage mode
        if (avoidStorage) {
            customClearFilters && customClearFilters();
        } else {
            clearFilters({
                entity,
                isAPurge: false,
                refresh: true,
                isFirstLoad: true,
                isEntityList: true,
                relatedEntity,
                clearAdvancedAndCrossFilters: true,
            });
        }
    }, [
        filters,
        clearSpecificFilter,
        useQuickFilters,
        clearFilters,
        entity,
        relatedEntity,
        avoidStorage,
        customClearFilters,
    ]);

    return {
        onChangeFilter,
        onDeleteFilter,
        onDeleteMultipleFilters,
        onClearFilters,
    };
};

export default useFilterOperations;
